import { useState, useEffect, useRef } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { GgcButton } from '@/components/ui/ggc/button';
import { MultiTargetCommentTag, type CommentDeleteTarget } from '@/components/MultiTargetCommentTag';

/* ── Inclusion Comment 셀: 태그 + 클릭 팝오버 ── */
export function IncCommentCell({ tokens, tokenCampaignMap, hideDetails, tokenTargets, tokenBlockedReasons, onRemoveFromTargets }: {
  tokens: string[];
  tokenCampaignMap: Record<string, { names: string[]; isSuggested?: boolean }>;
  hideDetails?: boolean;
  /** Admin edit context: per-token places the token can be deleted from. */
  tokenTargets?: Record<string, CommentDeleteTarget[]>;
  /** Admin edit context: per-token reason when there's no deletable target at all. */
  tokenBlockedReasons?: Record<string, string>;
  onRemoveFromTargets?: (token: string, campaignIds: string[]) => Promise<void>;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative">
      <div className="label-list">
        {tokens.slice(0, 5).map((tk) => {
          const info = tokenCampaignMap[tk];
          if (onRemoveFromTargets) {
            // Admin edit context: the × resolves where to delete (single confirm,
            // multi-target chooser, or disabled-with-reason when nothing is writable).
            return (
              <MultiTargetCommentTag
                key={tk}
                token={tk}
                isSuggested={info?.isSuggested}
                targets={tokenTargets?.[tk] ?? []}
                blockedReason={tokenBlockedReasons?.[tk]}
                onRemove={(cids) => onRemoveFromTargets(tk, cids)}
              />
            );
          }
          return <span key={tk} className={`ggc-tag ggc-tag--default${info?.isSuggested ? ' ggc-tag--suggested' : ''}`}>{tk}</span>;
        })}
        {tokens.length > 5 && <span className="text-xs text-ggc-disabled ml-1">+{tokens.length - 5}</span>}
      </div>
      {!hideDetails && (
        <GgcButton variant="primary" size="xs" className="mt-1 w-[60px]" onClick={() => setOpen((o) => !o)}>
          {t('Details', '상세보기')}
        </GgcButton>
      )}
      {open && !hideDetails && (
        <div className="est-price-popover est-price-popover--below" style={{ left: 0, right: 'auto' }}>
          <div className="est-price-popover-title">Inclusion comments</div>
          <div className="est-price-popover-body">
            {tokens.map((tk) => {
              const info = tokenCampaignMap[tk];
              return (
                <div key={tk} className="mb-2.5 last:mb-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`ggc-tag ggc-tag--default${info?.isSuggested ? ' ggc-tag--suggested' : ''}`}>{tk}</span>
                    {info?.isSuggested && <span className="italic text-ggc-cta-blue ggc-text-caption-s">default</span>}
                  </div>
                  {info?.names.length > 0 && (
                    <>
                      <div className="ggc-text-badge text-ggc-text-gray mb-0.5">Campaigns</div>
                      {info.names.map((n) => (
                        <div key={n} className="text-xs text-ggc-text-primary py-0.5 pl-1">{n}</div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
